import React from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { TCCredential } from 'src/api/generated';

/**
 * Props for CredentialDeleteModal component.
 */
export interface CredentialDeleteModalProps {
  /** Credential being deleted, or null when modal should be closed. */
  credential: TCCredential | null;
  /** Called when modal should be closed. */
  onClose: () => void;
  /** Called when deletion is confirmed. */
  onConfirm: (credentialId: string) => Promise<void>;
  /** Whether the delete action is in progress. */
  loading: boolean;
}

/**
 * CredentialDeleteModal component.
 */
export const CredentialDeleteModal: React.FC<CredentialDeleteModalProps> = ({
  credential,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useI18n();
  const isOpen = !!credential;

  const handleConfirm = async () => {
    if (!credential?.credentialId) return;

    try {
      await onConfirm(credential.credentialId);
      onClose();
    } catch {
      // onConfirm is expected to notify the user.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text size="h6" weight="semibold">
          {t('settings.credentials.delete.title')}
        </Text>
      }
      body={
        <div className="w-full space-y-2">
          <Text size="paragraph">
            {t('settings.credentials.delete.warning')}
          </Text>
          {credential && (
            <Text size="small" color="secondary">
              {credential.name ?? '-'} ({credential.provider ?? '-'})
            </Text>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-3 px-4 py-3">
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={onClose}
            disabled={loading}
          >
            {t('settings.credentials.actions.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleConfirm()}
            loading={loading}
            disabled={loading}
          >
            {t('settings.credentials.actions.delete')}
          </Button>
        </div>
      }
      dialogClassName="max-w-lg"
    />
  );
};
